import React, { useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Breadcrumbs, Button, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { MASTER_CRUD_REGISTRY } from '../masterData/masterCrudRegistry';

type BreadcrumbItem = {
  label: string;
  href?: string;
};

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/profile': 'Profile',
  '/customers': 'Customers',
  '/quotations': 'Quotations',
  '/sampling/samples': 'Samples',
  '/sampling/work-orders': 'Work orders',
  '/sampling/schedules': 'Penjadwalan Sampling',
  '/sampling/survey-schedule': 'Kalender Jadwal',
  '/sampling/sampling': 'Sampling',
  '/sampling/sample-preservation': 'Pengamanan sample',
  '/sampling/laboratory-results': 'Hasil uji lab',
  '/sampling/hasil-uji-letter': 'Hasil Uji Analis',
  '/keuangan/invoices': 'Invoice',
  '/masters': 'Master data',
  '/superadmin/permissions': 'Permission',
  '/superadmin/user-types': 'User types',
  '/superadmin/users': 'Users',
};

function cleanMasterTitle(title: string): string {
  return title.replace(/^Master\s+—\s+/i, '');
}

function sentenceCaseFromSegment(segment: string): string {
  const spaced = decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function buildBreadcrumbItems(pathname: string): BreadcrumbItem[] {
  if (pathname === '/' || pathname === '/dashboard') {
    return [{ label: 'Dashboard' }];
  }

  const parts = pathname.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [{ label: 'Dashboard', href: '/dashboard' }];

  if (parts[0] === 'masters') {
    items.push({ label: 'Master data', href: '/masters' });
    const slug = parts[1];
    if (slug) {
      items.push({
        label: cleanMasterTitle(MASTER_CRUD_REGISTRY[slug]?.title ?? sentenceCaseFromSegment(slug)),
      });
    }
    return items;
  }

  if (parts[0] === 'customers') {
    items.push({ label: 'Customers', href: '/customers' });
    if (parts[1] === 'new') items.push({ label: 'New customer' });
    return items;
  }

  if (parts[0] === 'customer') {
    items.push({ label: 'Customers', href: '/customers' });
    if (parts[2]) items.push({ label: sentenceCaseFromSegment(parts[2]) });
    return items;
  }

  if (parts[0] === 'quotations') {
    items.push({ label: 'Quotations', href: '/quotations' });
    if (parts[1] === 'new') items.push({ label: 'New quotation' });
    return items;
  }

  if (parts[0] === 'quotation') {
    items.push({ label: 'Quotations', href: '/quotations' });
    if (parts[2]) items.push({ label: sentenceCaseFromSegment(parts[2]) });
    if (parts[3] === 'work-order' && parts[4] === 'new') items.push({ label: 'New work order' });
    return items;
  }

  if (parts[0] === 'sampling') {
    if (parts[1] === 'survey-schedule') {
      items.push({ label: 'Jadwal Survey' });
      return items;
    }
    if (parts[1] === 'pre-survey') {
      items.push({ label: 'Pra-Survey', href: '/sampling/pre-survey' });
      if (parts[2] === 'work-order' && parts[3]) {
        items.push({ label: 'Checklist alat' });
      }
      return items;
    }
    if (parts[1] === 'survey') {
      items.push({ label: 'Sampling' });
      return items;
    }
    if (parts[1] === 'sampling') {
      items.push({ label: 'Sampling' });
      return items;
    }
    if (parts[1] === 'hasil-uji-letter') {
      items.push({ label: 'Hasil Uji Analis', href: '/sampling/hasil-uji-letter' });
      if (parts[2]) {
        let job = parts[2];
        try {
          job = decodeURIComponent(parts[2]);
        } catch {
          job = parts[2];
        }
        items.push({ label: job });
      }
      return items;
    }
    const base = parts[1] === 'samples' ? '/sampling/samples' : '/sampling/work-orders';
    items.push({ label: ROUTE_LABELS[base] ?? 'Sampling', href: base });
    if (parts[1] === 'work-order') {
      if (parts[3]) items.push({ label: sentenceCaseFromSegment(parts[3]) });
      if (parts[4] === 'sample' && parts[5] === 'new') items.push({ label: 'New sample' });
    }
    return items;
  }

  if (parts[0] === 'keuangan') {
    items.push({ label: 'Keuangan', href: '/keuangan/invoices' });
    if (parts[1] === 'invoices') items.push({ label: 'Invoice' });
    else if (parts[1]) items.push({ label: sentenceCaseFromSegment(parts[1]) });
    return items;
  }

  if (parts[0] === 'superadmin') {
    const base = `/${parts.slice(0, 2).join('/')}`;
    items.push({ label: ROUTE_LABELS[base] ?? 'Superadmin', href: base });
    if (parts[2] && parts[3]) items.push({ label: sentenceCaseFromSegment(parts[3]) });
    return items;
  }

  const exact = ROUTE_LABELS[pathname];
  if (exact) return [{ label: exact }];

  items.push({ label: sentenceCaseFromSegment(parts[parts.length - 1] ?? pathname) || 'Page' });
  return items;
}

type ContentBreadcrumbsProps = {
  /** When true, omit bottom margin so the trail can sit in a page chrome row. */
  embedded?: boolean;
};

export function ContentBreadcrumbs({ embedded = false }: ContentBreadcrumbsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const items = useMemo(() => buildBreadcrumbItems(location.pathname), [location.pathname]);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: embedded ? 0 : 0.75, flexShrink: 0, minHeight: 32 }}
    >
      <Button
        type="button"
        size="small"
        variant="text"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={() => navigate(-1)}
        sx={{ minWidth: 0, py: 0.25 }}
      >
        Kembali
      </Button>
      <Breadcrumbs aria-label="breadcrumb" sx={{ typography: 'body2', '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}>
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          if (isLast || !item.href) {
            return (
              <Typography key={`${item.label}-${idx}`} color="text.primary" variant="body2">
                {item.label}
              </Typography>
            );
          }
          return (
            <Link
              key={`${item.href}-${idx}`}
              component={RouterLink}
              to={item.href}
              underline="hover"
              color="inherit"
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Stack>
  );
}
